import { Form, Button, Row, Col, Table, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater, downloadExcel } from '../general/utils';
import SelectAgreement from '../general/selectAgreement';
import { C, semaphoreColor } from '../general/colors';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const YEARS  = Array.from({ length: 2050 - 2023 }, (_, i) => 2023 + i);

function DeltaCell({ delta, pct }) {
    if (delta === 0 && pct === null) return <span style={{ color: 'var(--abi-muted)', fontSize: 12 }}>—</span>;
    const up    = delta > 0;
    const color = up ? C.danger : C.success;
    return (
        <div style={{ color, fontWeight: 700, fontSize: 13 }}>
            <i className={`fa fa-arrow-${up ? 'up' : 'down'}`} style={{ marginRight: 3, fontSize: 10 }} />
            {currencyFormater(Math.abs(delta))}
            {pct !== null && (
                <div style={{ fontSize: 11 }}>{up ? '+' : ''}{pct.toFixed(1)}%</div>
            )}
        </div>
    );
}

function DepartmentReport({ setIsLoading, features }) {
    const [agreementFilter, setAgreementFilter] = useState('');
    const [yearFilter,  setYearFilter]  = useState(new Date().getFullYear());
    const [monthFilter, setMonthFilter] = useState(new Date().getMonth());
    const [message,     setMessage]     = useState('');
    const [messageType, setMessageType] = useState('info');

    const [currentData,  setCurrentData]  = useState([]);
    const [prevData,     setPrevData]     = useState([]);
    const [funds,        setFunds]        = useState([]);
    const [departments,  setDepartments]  = useState([]);

    // Mes anterior: si enero → diciembre del año anterior
    const currMonth = parseInt(monthFilter) + 1;
    const prevMonth = monthFilter === 0 ? 12 : parseInt(monthFilter);
    const prevYear  = monthFilter === 0 ? parseInt(yearFilter) - 1 : parseInt(yearFilter);

    const currMonthLabel = MONTHS[monthFilter];
    const prevMonthLabel = MONTHS[monthFilter === 0 ? 11 : monthFilter - 1];

    useEffect(() => {
        if (agreementFilter) fetchAll();
        else {
            setCurrentData([]); setPrevData([]); setFunds([]); setDepartments([]);
            setMessageType('info');
            setMessage('Selecciona un contrato para ver el reporte');
        }
    }, [agreementFilter, yearFilter, monthFilter]);

    const fetchAll = async () => {
        setIsLoading(true);
        try {
            const [cur, prev, fundsRes, deptsRes] = await Promise.all([
                axiosInstance.get('/reports/monthly/' + agreementFilter, { params: { year: yearFilter,  month: currMonth } }),
                axiosInstance.get('/reports/monthly/' + agreementFilter, { params: { year: prevYear,    month: prevMonth  } }),
                axiosInstance.get('/funds/agreements/' + agreementFilter),
                axiosInstance.get('/departments/agreements/' + agreementFilter),
            ]);
            setCurrentData(cur.data    || []);
            setPrevData(prev.data      || []);
            setFunds(fundsRes.data     || []);
            setDepartments(deptsRes.data || []);
            if (!(deptsRes.data || []).length) {
                setMessageType('info');
                setMessage('Sin centros de costo en este contrato');
            } else { setMessage(''); }
        } catch (e) { console.log(e); }
        finally { setIsLoading(false); }
    };

    // Agrupación frontend por departamento
    const aggByDept = (data) => {
        const map = {};
        data.forEach(r => {
            const id = r.department_id;
            if (!map[id]) map[id] = { name: r.department, total: 0 };
            map[id].total += parseFloat(r.total_amount || 0);
        });
        return map;
    };

    const rows = useMemo(() => {
        const curMap  = aggByDept(currentData);
        const prevMap = aggByDept(prevData);

        // Presupuesto por departamento (suma de fondos)
        const budgetMap = {};
        funds.forEach(f => {
            const did = f.department?.id;
            if (!did) return;
            if (!budgetMap[did]) budgetMap[did] = 0;
            budgetMap[did] += parseFloat(f.amount || 0);
        });

        return departments.map(dept => {
            const cur      = curMap[dept.id]?.total  || 0;
            const prev     = prevMap[dept.id]?.total || 0;
            const delta    = cur - prev;
            const deltaPct = prev > 0 ? (delta / prev) * 100 : null;
            const budget   = budgetMap[dept.id] || 0;
            const budgetPct = budget > 0 ? Math.min(100, (cur / budget) * 100) : 0;
            return { id: dept.id, name: dept.name, cur, prev, delta, deltaPct, budget, budgetPct };
        }).sort((a, b) => b.cur - a.cur);
    }, [currentData, prevData, funds, departments]);

    const totals = useMemo(() => ({
        cur:    rows.reduce((s, r) => s + r.cur,    0),
        prev:   rows.reduce((s, r) => s + r.prev,   0),
        budget: rows.reduce((s, r) => s + r.budget, 0),
    }), [rows]);

    const totalDelta    = totals.cur - totals.prev;
    const totalDeltaPct = totals.prev > 0 ? (totalDelta / totals.prev) * 100 : null;
    const maxCur        = Math.max(...rows.map(r => r.cur), 1);

    const hasData = rows.some(r => r.cur > 0 || r.prev > 0);

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>Consumo mensual por Centro de Costo:</strong> compara el gasto de cada departamento
                    entre el mes seleccionado y el mes anterior.
                    La columna <em>Δ Variación</em> muestra el incremento en{' '}
                    <span style={{ color: C.danger, fontWeight: 700 }}>rojo</span> y la reducción en{' '}
                    <span style={{ color: C.success, fontWeight: 700 }}>verde</span>.
                    La barra de ejecución compara el gasto del mes contra el presupuesto total del departamento.
                </div>
            </div>

            {/* ── Filtros ── */}
            <div className="order-section">
                <div className="order-section-header">
                    <i className="fa fa-building-o" /> Consumo mensual por Centro de Costo
                </div>
                <div className="order-section-body">
                    <Row className="g-3 align-items-end">
                        <Col md={2} xs={6}>
                            <Form.Label className="report-field-label">Año</Form.Label>
                            <Form.Select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={2} xs={6}>
                            <Form.Label className="report-field-label">Mes</Form.Label>
                            <Form.Select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
                                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={8} xs={12}>
                            <Form.Label className="report-field-label">Contrato</Form.Label>
                            <SelectAgreement agreement={agreementFilter} setAgreement={setAgreementFilter} disabled={false} />
                        </Col>
                    </Row>
                </div>
            </div>

            {message && <Alert variant={messageType} className="mt-2">{message}</Alert>}

            {departments.length > 0 && (
                <>
                    {/* ── Toolbar ── */}
                    <div className="report-toolbar">
                        <div>
                            <span className="report-count">
                                <strong>{departments.length}</strong> centro{departments.length !== 1 ? 's' : ''} de costo —{' '}
                                comparando <strong>{currMonthLabel} {yearFilter}</strong> vs <strong>{prevMonthLabel} {prevYear}</strong>
                            </span>
                            {!hasData && (
                                <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--abi-muted)' }}>
                                    <i className="fa fa-info-circle" style={{ marginRight: 4 }} />
                                    Sin consumo en este período
                                </span>
                            )}
                        </div>
                        <Button variant="outline-success" size="sm" onClick={() => {
                            const headers = [
                                'Centro de Costo',
                                `Consumo ${currMonthLabel} ${yearFilter}`,
                                `Consumo ${prevMonthLabel} ${prevYear}`,
                                'Δ Variación',
                                '% Variación',
                                'Presupuesto total',
                                '% Ejecución mes',
                            ];
                            const exRows = rows.map(r => [
                                r.name,
                                parseFloat(r.cur.toFixed(2)),
                                parseFloat(r.prev.toFixed(2)),
                                parseFloat(r.delta.toFixed(2)),
                                r.deltaPct !== null ? parseFloat(r.deltaPct.toFixed(2)) : 0,
                                parseFloat(r.budget.toFixed(2)),
                                parseFloat(r.budgetPct.toFixed(2)),
                            ]);
                            const colFormats = [null, 'currency', 'currency', 'currency', 'percent', 'currency', 'percent'];
                            downloadExcel(headers, exRows, `Consumo por Departamento ${currMonthLabel} ${yearFilter}`, 'Departamentos', colFormats);
                        }}>
                            <i className="fa fa-file-excel-o" /> Excel
                        </Button>
                    </div>

                    {/* ── Tabla ── */}
                    <Table responsive className="report-table">
                        <thead>
                            <tr>
                                <th>Centro de Costo</th>
                                <th className="text-end">{currMonthLabel} {yearFilter}</th>
                                <th className="text-end d-none d-md-table-cell">{prevMonthLabel} {prevYear}</th>
                                <th className="text-end">Δ Variación</th>
                                <th className="d-none d-lg-table-cell">Ejecución vs presupuesto</th>
                                <th className="text-end d-none d-lg-table-cell">Presupuesto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const bc = semaphoreColor(r.budgetPct, features);
                                const barWidth = maxCur > 0 ? (r.cur / maxCur) * 100 : 0;
                                return (
                                    <tr key={'dept-' + r.id}>
                                        <td>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                                            <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>ID {r.id}</div>
                                        </td>
                                        <td className="text-end">
                                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                                                {r.cur > 0 ? currencyFormater(r.cur) : <span style={{ color: 'var(--abi-muted)', fontSize: 12 }}>—</span>}
                                            </div>
                                            {/* Mini barra proporcional al máximo */}
                                            {r.cur > 0 && (
                                                <div style={{ marginTop: 4, height: 3, background: 'var(--abi-border)', borderRadius: 999, overflow: 'hidden' }}>
                                                    <div style={{ width: `${barWidth}%`, height: '100%', background: 'var(--abi-primary)', borderRadius: 999 }} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-end d-none d-md-table-cell" style={{ color: 'var(--abi-muted)', fontSize: 13 }}>
                                            {r.prev > 0 ? currencyFormater(r.prev) : '—'}
                                        </td>
                                        <td className="text-end">
                                            <DeltaCell delta={r.delta} pct={r.deltaPct} />
                                        </td>
                                        <td className="d-none d-lg-table-cell" style={{ minWidth: 180 }}>
                                            {r.budget > 0 ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div className="report-fund-bar-wrap" style={{ flex: 1 }}>
                                                        <div className="report-fund-bar-fill" style={{ width: `${r.budgetPct}%`, background: bc }} />
                                                    </div>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: bc, minWidth: 38, textAlign: 'right' }}>
                                                        {r.budgetPct.toFixed(1)}%
                                                    </span>
                                                </div>
                                            ) : (
                                                <span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>Sin presupuesto asignado</span>
                                            )}
                                        </td>
                                        <td className="text-end d-none d-lg-table-cell" style={{ fontSize: 12, color: 'var(--abi-muted)' }}>
                                            {r.budget > 0 ? currencyFormater(r.budget) : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="report-tfoot">
                                <th>Total</th>
                                <th className="text-end" style={{ fontSize: 15 }}>{currencyFormater(totals.cur)}</th>
                                <th className="text-end d-none d-md-table-cell" style={{ fontSize: 13 }}>{currencyFormater(totals.prev)}</th>
                                <th className="text-end">
                                    <DeltaCell delta={totalDelta} pct={totalDeltaPct} />
                                </th>
                                <th className="d-none d-lg-table-cell">
                                    {totals.budget > 0 && (() => {
                                        const pct = Math.min(100, (totals.cur / totals.budget) * 100);
                                        const bc  = semaphoreColor(pct, features);
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.25)', borderRadius: 999, overflow: 'hidden' }}>
                                                    <div style={{ width: `${pct}%`, height: '100%', background: '#fff', borderRadius: 999 }} />
                                                </div>
                                                <span style={{ fontSize: 11, fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
                                                    {pct.toFixed(1)}%
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </th>
                                <th className="text-end d-none d-lg-table-cell">{totals.budget > 0 ? currencyFormater(totals.budget) : ''}</th>
                            </tr>
                        </tfoot>
                    </Table>
                </>
            )}
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading, features: state.features }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(DepartmentReport);
